import React, { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CheckCircle, XCircle } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { notifyBookingConfirmed, notifyBookingCancelled } from '@/utils/bookingNotifications';

interface HandoffData {
  timestamp: number;
  channel: 'playtomic' | 'whatsapp';
  clubName?: string | null;
  matchId?: string | null;
  groupId?: string | null;
  playerIds?: string[];
  matchDate?: string | null;
  rosterLabel?: string | null;
}

const HANDOFF_KEY = 'booking_handoff';
const THIRTY_MINUTES = 30 * 60 * 1000;

/**
 * After returning from Playtomic or WhatsApp (detected via visibilitychange),
 * shows a small prompt asking if the user booked a court.
 * If yes → asks for court name/number, then confirms.
 * If no → deletes the pending booking_feedback row to unlock the match.
 */
export const BookingFeedbackPrompt: React.FC = () => {
  const { user, profile } = useAuth();
  const queryClient = useQueryClient();
  const [show, setShow] = useState(false);
  const [handoff, setHandoff] = useState<HandoffData | null>(null);
  const [step, setStep] = useState<'ask' | 'court-details'>('ask');
  const [courtName, setCourtName] = useState('');
  const submittingRef = useRef(false);

  const checkForPendingFeedback = useCallback(() => {
    if (!user) return;

    const raw = localStorage.getItem(HANDOFF_KEY);
    if (!raw) return;

    try {
      const data: HandoffData = JSON.parse(raw);
      if (Date.now() - data.timestamp < THIRTY_MINUTES) {
        setHandoff(data);
        setStep('ask');
        setCourtName('');
        setShow(true);
      } else {
        // Expired — clean up
        localStorage.removeItem(HANDOFF_KEY);
      }
    } catch {
      localStorage.removeItem(HANDOFF_KEY);
    }
  }, [user]);

  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === 'visible') {
        setTimeout(checkForPendingFeedback, 1500);
      }
    };

    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [checkForPendingFeedback]);

  const cleanup = () => {
    localStorage.removeItem(HANDOFF_KEY);
    setShow(false);
    setHandoff(null);
    setStep('ask');
    setCourtName('');
    submittingRef.current = false;
  };

  const invalidateBooking = () => {
    if (handoff?.matchId) {
      queryClient.invalidateQueries({ queryKey: ['match-booking-status', handoff.matchId] });
    }
  };

  const buildCtx = (h: HandoffData) => ({
    matchId: h.matchId!,
    groupId: h.groupId,
    playerIds: h.playerIds || [],
    currentUserId: user!.id,
    matchDate: h.matchDate || undefined,
    clubName: h.clubName || undefined,
    rosterLabel: h.rosterLabel || undefined,
  });

  const handleNo = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    // Clear localStorage immediately to prevent re-triggers
    localStorage.removeItem(HANDOFF_KEY);

    if (user && handoff?.matchId) {
      try {
        const { count } = await supabase
          .from('booking_feedback')
          .delete()
          .eq('match_id', handoff.matchId)
          .eq('user_id', user.id)
          .is('booked', null);
        invalidateBooking();
        // Only notify if a row was actually deleted
        if (count && count > 0) {
          notifyBookingCancelled(
            buildCtx(handoff),
            profile?.full_name || 'A player'
          );
        }
      } catch (e) {
        console.error('Failed to delete pending booking feedback:', e);
      }
    }
    cleanup();
  };

  const handleYes = () => {
    setStep('court-details');
  };

  const handleConfirmCourt = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    // Clear localStorage immediately to prevent re-triggers
    localStorage.removeItem(HANDOFF_KEY);

    if (user && handoff?.matchId) {
      try {
        const { count } = await supabase
          .from('booking_feedback')
          .update({
            booked: true,
            court_name_or_number: courtName.trim() || null,
          })
          .eq('match_id', handoff.matchId)
          .eq('user_id', user.id)
          .is('booked', null);
        invalidateBooking();
        // Only notify if a row was actually updated
        if (count && count > 0) {
          notifyBookingConfirmed(
            buildCtx(handoff),
            profile?.full_name || 'A player',
            courtName.trim() || undefined
          );
        }
      } catch (e) {
        console.error('Failed to confirm booking feedback:', e);
      }
    }
    cleanup();
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 z-50 flex justify-center">
      <div className="bg-card border border-border rounded-xl shadow-lg p-4 max-w-sm w-full animate-in slide-in-from-bottom-4 duration-300">
        {step === 'ask' && (
          <>
            <p className="text-sm font-medium text-foreground mb-3 text-center">
              Did you book a court? 🎾
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="flex-1 gap-1.5"
                onClick={handleNo}
              >
                <XCircle className="h-4 w-4" />
                No
              </Button>
              <Button
                size="sm"
                className="flex-1 gap-1.5"
                onClick={handleYes}
              >
                <CheckCircle className="h-4 w-4" />
                Yes!
              </Button>
            </div>
          </>
        )}

        {step === 'court-details' && (
          <>
            <p className="text-sm font-medium text-foreground mb-3 text-center">
              Which court? 🏟️
            </p>
            <div className="space-y-2">
              <Input
                placeholder="Court name or number (optional)"
                value={courtName}
                onChange={(e) => setCourtName(e.target.value)}
                className="h-9 text-sm"
                autoFocus
              />
              <Button
                size="sm"
                className="w-full gap-1.5"
                onClick={handleConfirmCourt}
              >
                <CheckCircle className="h-4 w-4" />
                Confirm Booking
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
